import { User, Phone, MapPin, StickyNote, ClipboardCheck } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section, Chip, Field } from "@/components/LeadSections";
import { TIPI_SCHEDA, REPARTI, TIPOLOGIE_CLIENTE, emptyReferente } from "@/lib/clienteConstants";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const Lbl = ({ children, required }) => (
  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children} {required && <span className="text-tricolor-red">*</span>}
  </Label>
);

const SingleChoice = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((o) => (
      <Chip key={o} label={o} testid={`${prefix}-${slug(o)}`} active={value === o} onClick={() => onChange(value === o ? "" : o)} />
    ))}
  </div>
);

const Address = ({ form, set, prefix, testPrefix }) => (
  <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
    <div className="md:col-span-6">
      <Field label="Via / Piazza" testid={`input-${testPrefix}-via`} value={form[`${prefix}_via`]} onChange={(v) => set(`${prefix}_via`, v)} />
    </div>
    <div className="md:col-span-2"><Field label="CAP" testid={`input-${testPrefix}-cap`} value={form[`${prefix}_cap`]} onChange={(v) => set(`${prefix}_cap`, v)} /></div>
    <div className="md:col-span-3"><Field label="Città" testid={`input-${testPrefix}-citta`} value={form[`${prefix}_citta`]} onChange={(v) => set(`${prefix}_citta`, v)} /></div>
    <div className="md:col-span-1"><Field label="Prov." testid={`input-${testPrefix}-provincia`} value={form[`${prefix}_provincia`]} onChange={(v) => set(`${prefix}_provincia`, v)} /></div>
  </div>
);

const ReferenteBlock = ({ r, onChange }) => {
  const s = slug(r.reparto);
  const f = (k, label, type = "text") => (
    <Field label={label} testid={`referente-${s}-${k}`} type={type} value={r[k]} onChange={(v) => onChange({ ...r, [k]: v })} />
  );
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-4 animate-in fade-in slide-in-from-top-2" data-testid={`referente-block-${s}`}>
      <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-buscema-green flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-buscema-gold" /> Referente {r.reparto}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {f("nome", "Nome e cognome")}
        {f("ruolo", "Ruolo")}
        {f("telefono", "Telefono", "tel")}
        {f("cellulare", "Cellulare", "tel")}
        <div className="md:col-span-2">{f("email", "E-mail", "email")}</div>
      </div>
    </div>
  );
};

const Referenti = ({ referenti, set }) => {
  const has = (rep) => referenti.some((r) => r.reparto === rep);
  const toggle = (rep) =>
    set("referenti", has(rep)
      ? referenti.filter((r) => r.reparto !== rep)
      : [...referenti, emptyReferente(rep)].sort((a, b) => REPARTI.indexOf(a.reparto) - REPARTI.indexOf(b.reparto)));
  const update = (nr) => set("referenti", referenti.map((r) => (r.reparto === nr.reparto ? nr : r)));
  const ordered = REPARTI.filter(has).map((rep) => referenti.find((r) => r.reparto === rep));
  return (
    <>
      <div className="space-y-2" data-testid="reparto-group">
        <Lbl required>Reparti referenti (puoi sceglierne più di uno)</Lbl>
        <div className="flex flex-wrap gap-2.5">
          {REPARTI.map((rep) => (
            <Chip key={rep} label={rep} testid={`reparto-${slug(rep)}`} active={has(rep)} onClick={() => toggle(rep)} />
          ))}
        </div>
      </div>
      {ordered.map((r) => <ReferenteBlock key={r.reparto} r={r} onChange={update} />)}
    </>
  );
};

const SubTitle = ({ children }) => <h3 className="font-heading text-sm font-semibold text-buscema-green border-b border-gray-100 pb-2">{children}</h3>;

export const ClienteSections = ({ form, set }) => (
  <>
    <Section icon={ClipboardCheck} title="Tipo Scheda">
      <div className="space-y-2" data-testid="tipo-scheda-group">
        <Lbl required>Contatto nuovo o aggiornamento?</Lbl>
        <SingleChoice options={TIPI_SCHEDA} value={form.tipo_scheda} onChange={(v) => set("tipo_scheda", v)} prefix="tipo-scheda" />
      </div>
      <Field label="Data inserimento" testid="input-data-inserimento" type="date" value={form.data_inserimento} onChange={(v) => set("data_inserimento", v)} />
    </Section>

    <Section icon={User} title="Dati Anagrafici">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Ragione sociale / Nome e cognome" testid="input-ragione-sociale" value={form.ragione_sociale} onChange={(v) => set("ragione_sociale", v)} />
        <Field label="Insegna / Nome commerciale" testid="input-insegna" value={form.insegna} onChange={(v) => set("insegna", v)} />
      </div>
      <div className="space-y-2">
        <Lbl>Tipologia cliente</Lbl>
        <SingleChoice options={TIPOLOGIE_CLIENTE} value={form.tipologia_cliente} onChange={(v) => set("tipologia_cliente", v)} prefix="tipologia-cliente" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Partita IVA" testid="input-partita-iva" value={form.partita_iva} onChange={(v) => set("partita_iva", v)} />
        <Field label="Codice fiscale" testid="input-codice-fiscale" value={form.codice_fiscale} onChange={(v) => set("codice_fiscale", v)} />
        <Field label="Codice SDI" testid="input-codice-sdi" value={form.codice_sdi} onChange={(v) => set("codice_sdi", v)} />
        <Field label="PEC" testid="input-pec" type="email" value={form.pec} onChange={(v) => set("pec", v)} />
        <Field label="Settore di attività" testid="input-settore" value={form.settore} onChange={(v) => set("settore", v)} />
        <Field label="Sito web" testid="input-sito-web" type="url" value={form.sito_web} onChange={(v) => set("sito_web", v)} />
      </div>
    </Section>

    <Section icon={Phone} title="Contatti">
      <Referenti referenti={form.referenti || []} set={set} />
      <Field label="E-mail amministrazione" testid="input-email-amministrazione" type="email" value={form.email_amministrazione} onChange={(v) => set("email_amministrazione", v)} />
    </Section>

    <Section icon={MapPin} title="Indirizzi">
      <SubTitle>Sede legale</SubTitle>
      <Address form={form} set={set} prefix="sede" testPrefix="sede" />
      <SubTitle>Indirizzo di consegna, se diverso</SubTitle>
      <Address form={form} set={set} prefix="consegna" testPrefix="consegna" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Referente consegna" testid="input-consegna-referente" value={form.consegna_referente} onChange={(v) => set("consegna_referente", v)} />
        <Field label="Telefono" testid="input-consegna-telefono" type="tel" value={form.consegna_telefono} onChange={(v) => set("consegna_telefono", v)} />
        <Field label="Orari ricevimento merce" testid="input-consegna-orari" value={form.consegna_orari} onChange={(v) => set("consegna_orari", v)} />
      </div>
      <SubTitle>Indirizzo di fatturazione, se diverso</SubTitle>
      <Address form={form} set={set} prefix="fatturazione" testPrefix="fatturazione" />
    </Section>

    <Section icon={StickyNote} title="Note">
      <Textarea
        data-testid="input-note"
        value={form.note}
        onChange={(e) => set("note", e.target.value)}
        placeholder="Note libere dell'operatore..."
        rows={5}
        className="rounded-xl text-base resize-none"
      />
      <Field label="Data ultimo aggiornamento" testid="input-data-aggiornamento" type="date" value={form.data_aggiornamento} onChange={(v) => set("data_aggiornamento", v)} />
    </Section>
  </>
);
